import type { RelationshipPair } from '../../entities/graph-document';
import { canonicalRelationshipPairId } from './normalize-explicit-relationship-pair';

export interface RelationshipValidationResult<TValue = undefined> {
  valid: boolean;
  errors: string[];
  value?: TValue;
}

export function validateRelationshipsCollection(value: unknown): RelationshipValidationResult<RelationshipPair[]> {
  if (!Array.isArray(value)) {
    return {
      valid: false,
      errors: ['relationships array is required'],
    };
  }

  const pairIds = new Set<string>();
  const errors: string[] = [];

  for (const pair of value) {
    if (!pair || typeof pair !== 'object') {
      errors.push('relationships entries must be objects');
      continue;
    }

    const candidatePair = pair as Partial<RelationshipPair>;
    if (typeof candidatePair.pairId !== 'string') {
      errors.push('relationship pairId is required');
      continue;
    }

    if (pairIds.has(candidatePair.pairId)) {
      errors.push('duplicate relationship pair ids are not allowed');
      continue;
    }

    pairIds.add(candidatePair.pairId);
  }

  return {
    valid: errors.length === 0,
    errors,
    value: errors.length === 0 ? value as RelationshipPair[] : undefined,
  };
}

export function validateExplicitRelationshipPair(
  pair: RelationshipPair,
  existingNodeIds: Iterable<string>
): RelationshipValidationResult<RelationshipPair> {
  const errors: string[] = [];
  const nodeIdSet = new Set(existingNodeIds);

  if (pair.endpoints.length !== 2) {
    errors.push('relationship pair must contain exactly two endpoints');
  }

  const [firstNodeId, secondNodeId] = pair.endpoints;

  if (firstNodeId === secondNodeId) {
    errors.push('self relationships are not allowed');
  }

  if (!nodeIdSet.has(firstNodeId) || !nodeIdSet.has(secondNodeId)) {
    errors.push('relationship pair references a missing node');
  }

  if (pair.pairId !== canonicalRelationshipPairId(firstNodeId, secondNodeId)) {
    errors.push('relationship pairId must match canonical endpoint ordering');
  }

  if (pair.directions.length !== 2) {
    errors.push('relationship pair must contain exactly two directions');
  }

  const expectedDirections = new Set([
    `${firstNodeId}->${secondNodeId}`,
    `${secondNodeId}->${firstNodeId}`,
  ]);

  for (const direction of pair.directions) {
    const directionKey = `${direction.sourceNodeId}->${direction.targetNodeId}`;

    if (!expectedDirections.has(directionKey)) {
      errors.push('relationship directions must align with the canonical endpoint ordering');
    }

    const uniqueLabels = new Set(direction.labels);
    if (uniqueLabels.size !== direction.labels.length) {
      errors.push('duplicate relationship labels are not allowed within the same direction');
    }
  }

  if (pair.directions.every((direction) => direction.labels.length === 0)) {
    errors.push('at least one relationship label is required');
  }

  return {
    valid: errors.length === 0,
    errors,
    value: errors.length === 0 ? pair : undefined,
  };
}